import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  isInvalidProduct,
  onTapTapCustomerPaymentAddFromTransactionPayments,
  taptapAddCustomerFields,
  taptapCreateTicketLogs,
  addTicketComment,
  taptapGetCustomerDetailsViaTransactionNo,
  taptapServiceType,
  taptapSource,
  taptapStatuses,
  transactionPaymentsToTapTapCustomerPaymentPayloadTransformer,
  taptapServiceTypesWithSalesTransferBranch
} from 'src/redux/slices/taptap-customer';
import {
  PendingTapTapInternalCustomer,
  TapTapCustomerData
} from 'src/redux/slices/taptap-customer/types';
import { cloneDeep, isEmpty } from 'lodash';
import TapTapForm from './components/TapTapForm';
import { AlertDialog, LoaderBar, LoadingButton, Page } from 'src/components';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { SupabaseClientService } from 'src/services';
import { useSnackBar } from 'src/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  GetTransactionPaymentsResponse,
  GetTransactionsViaTransactionNoResponse,
  TemplateDetails,
  TemplateProductDetails,
  Transaction
} from 'src/types';
import TapTapQuotationCard, {
  TestingItem
} from './components/TapTapQuotationCard';
import SearchIcon from '@material-ui/icons/Search';
import { taptapCustomerField } from 'src/redux/slices/taptap-customer/constants/taptap-fields';
import { optionalEmailSchema } from 'src/redux/slices/taptap-customer/constants/taptap-validation';
import { cleanSN, sleep } from 'src/utils';
import { TapTapPaymentsCard } from './components/TapTapPaymentsCard';
import { TapTapTransactionsCard } from './components/TapTapTransactionsCard';
import PowerIcon from '@material-ui/icons/Power';
import { TapTapPowerBuildsCard } from './components/TapTapPowerBuildsCard';
import { sortProductsByCategory } from 'src/utils/categories/sortByCategory';
import useUserInfo from 'src/hooks/user/use-user-info';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';

interface FormValues {
  [key: string]: any; // could string, boolean, number, etc
}

type RetryFailureStep =
  | 'network'
  | 'quotation-validation'
  | 'internal-customer'
  | 'internal-transaction'
  | 'taptap-verification'
  | 'taptap-ticket';

interface RetryFailure {
  step: RetryFailureStep;
  reserved: boolean;
}

interface RetryFailureMessageMap {
  network: { title: string; description: string };
  'quotation-validation': { title: string; description: string };
  'internal-customer': { title: string; description: string };
  'internal-transaction': { title: string; description: string };
  'taptap-verification': { title: string; description: string };
  'taptap-ticket': { title: string; description: string };
}

const retryFailureMessages: RetryFailureMessageMap = {
  network: {
    title: 'No internet connection',
    description:
      'The creation process did not continue. Retry will check the connection and resume from the first incomplete step.'
  },
  'quotation-validation': {
    title: 'Quotation validation failed',
    description:
      'Retry will validate the quotation and product availability again, then continue the creation process.'
  },
  'internal-customer': {
    title: 'Internal customer creation failed',
    description:
      'Retry will create the internal customer, then continue with the internal transaction and TapTap ticket.'
  },
  'internal-transaction': {
    title: 'Internal transaction creation failed',
    description:
      'The saved internal customer will be reused. Retry will create the internal transaction, then continue with the TapTap ticket.'
  },
  'taptap-verification': {
    title: 'TapTap ticket status could not be verified',
    description:
      'No new ticket was created because the existing ticket check failed. Retry will check again first and will only create a ticket if none exists.'
  },
  'taptap-ticket': {
    title: 'TapTap ticket creation failed',
    description:
      'The existing internal customer and transaction will be kept. Retry will only create the missing TapTap ticket.'
  }
};

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100%',
    paddingBottom: 20,
    paddingTop: 20
  },
  contactCard: {
    marginTop: 20
  }
}));

const initialValues: FormValues = {
  [taptapCustomerField.STATUS]: taptapStatuses.FOR_PAYMENT,
  first_name: '',
  last_name: '',
  contact_no: '',
  email: '',
  address: '',
  source: '',
  online_sales_representative: '',
  fb_transaction_type: '',
  viber_transaction_type: '',
  ecomm_transaction_type: '',
  branch_released: '',
  courier: '',
  service_type: '',
  kind_of_sales_transfer: '',
  total_amount: 0,
  shipping_fee: 0,
  is_shipping_fee_paid: false,
  is_agree_tnc: true,
  trial_os: '',
  [taptapCustomerField.PURCHASE_PURPOSE]: '',
  [taptapCustomerField.REFERRAL_SOURCE]: ''
};

const SALES_TRANSFER_COMMENT_TAG = 'SALES_TRANSFER';

const buildSalesTransferComment = (kindOfSalesTransfer?: string) => {
  if (!kindOfSalesTransfer) {
    return '';
  }

  return `Sales Transfer - ${kindOfSalesTransfer}`;
};

const getSalesTransferTicketIdFromSearch = (search: string) => {
  const ticketId = new URLSearchParams(search).get('sales_transfer_ticket_id');

  return ticketId ? Number(ticketId) : undefined;
};

const resolveInitialTicketStatus = ({
  isReservedTicket,
  values
}: {
  isReservedTicket: boolean;
  values: FormValues;
}) => {
  if (
    taptapServiceTypesWithSalesTransferBranch.includes(
      values?.[taptapCustomerField.SERVICE_TYPE]
    )
  ) {
    return taptapStatuses.PENDING_SALES_TRANSFER;
  }

  if (isReservedTicket) {
    return taptapStatuses.FOR_PAYMENT;
  }

  return values?.[taptapCustomerField.SOURCE] === taptapSource.ECOMM
    ? taptapStatuses.ONLINE_FUND_TRANSFER
    : taptapStatuses.FOR_PAYMENT;
};

const { actions: pcBundleActions } = slices.pcBundles;
const { actions: quotationActions } = slices.quotations;
const { actions: createCustomerServiceActions } = slices.customerService;
const { actions: transactionActions } = slices.transaction;
const { actions: transactionPaymentActions } = slices.transactionPayments;
const {
  actions: taptapCustomerActions,
  selectors: taptapCustomerSelectors
} = slices.taptapCustomer;

const AddTapTapCustomer = () => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userDetails } = useUserInfo();
  const pendingInternalCustomer = useAppSelector(
    taptapCustomerSelectors.selectPendingInternalCustomer
  );

  const isLoading = useRef(false);
  const isReserved = useRef(false); // <-- NEW: flag para sa reserved submit
  const pendingInternalCustomerRef = useRef<
    PendingTapTapInternalCustomer | undefined
  >(pendingInternalCustomer);
  const salesTransferTicketId = useMemo(
    () => getSalesTransferTicketIdFromSearch(location.search),
    [location.search]
  );
  const isSalesTransferAttachMode = Boolean(salesTransferTicketId);

  const [quotationItemsConcerns, setQuotationItemsConcerns] = useState<
    string[]
  >();
  const [quotationResponse, setQuotationResponse] = useState<TemplateDetails>();
  const [testingCheckedItems, setTestingCheckedItems] = useState<TestingItem[]>(
    []
  );
  const [quotationProductsLoading, setQuotationProductsLoading] = useState<
    boolean
  >(false);

  const [transactionsInfoLoading, setTransactionsInfoLoading] = useState<
    boolean
  >(false);
  const [transactionNoInput, setTransactionNoInput] = useState<string>('');
  const [transactionInfoResponse, setTransactionInfoResponse] = useState<
    GetTransactionsViaTransactionNoResponse
  >();
  const [
    transactionPaymentsResponse,
    setTransactionPaymentsResponse
  ] = useState<GetTransactionPaymentsResponse>();

  const [poweredBuildSNInput, setPoweredBuildSNInput] = useState<string>('');
  const [poweredBuildSNResponse, setPoweredBuildSNResponse] = useState<
    GetTransactionsViaTransactionNoResponse // CHANGE THIS TO POWERED BUILD RESPONSE
  >();
  const [retryFailure, setRetryFailure] = useState<RetryFailure | undefined>(
    pendingInternalCustomer
      ? {
          step: pendingInternalCustomer.internal_transaction_no
            ? 'taptap-ticket'
            : 'internal-transaction',
          reserved: Boolean(pendingInternalCustomer.is_reserved)
        }
      : undefined
  );
  const [isRetryDialogVisible, setIsRetryDialogVisible] = useState(false);

  const showRetryDialog = (step: RetryFailureStep) => {
    setRetryFailure({
      step,
      reserved: isReserved.current
    });
    setIsRetryDialogVisible(true);
  };

  const resetFormAlongWithOtherStates = () => {
    formik.resetForm();
    pendingInternalCustomerRef.current = undefined;
    dispatch(taptapCustomerActions.clearPendingInternalCustomer());
    setRetryFailure(undefined);
    setIsRetryDialogVisible(false);
    setQuotationItemsConcerns(undefined);
    setQuotationResponse(undefined);
    setTestingCheckedItems([]);

    // Transactions
    setTransactionNoInput('');
    setTransactionPaymentsResponse(undefined);
    setTransactionInfoResponse(undefined);
    setTransactionsInfoLoading(false);

    // Powered Builds
    setPoweredBuildSNInput('');
    setPoweredBuildSNResponse(undefined);
  };

  // eslint-disable-next-line no-unused-vars
  const onInputOfPoweredBuildsSN = async (poweredBuildSNArg: string) => {
    let newFormDataInitial = {
      [taptapCustomerField.INTERNAL_QUOTATION_ID]: null,
      [taptapCustomerField.INTERNAL_TRANSACTION_NO]: null,
      [taptapCustomerField.TOTAL_AMOUNT]: 0,
      [taptapCustomerField.BRANCH_RELEASED]: ''
    };

    const poweredBuildSN = cleanSN(poweredBuildSNArg);

    try {
      const fetchPoweredBuildComponentsResponse = unwrapResult(
        await dispatch(
          pcBundleActions.getComponentGroupItemsViaSN(poweredBuildSN)
        )
      );
      if (!fetchPoweredBuildComponentsResponse?.success) {
        if (!poweredBuildSN) {
          snackBar.show({
            severity: 'error',
            message:
              'Something went wrong while fetching powered build components',
            useSound: true
          });
          return;
        }
      }

      const pcBundleItems =
        fetchPoweredBuildComponentsResponse?.originalData?.data || [];

      if (pcBundleItems?.length <= 0) {
        snackBar.show({
          severity: 'error',
          message: 'power build component is empty',
          useSound: true
        });
        return;
      }

      // TODO: Add checker to see if they are same branches

      const poweredBuildItemsToTransactionLookAlikeItems: Transaction[] = pcBundleItems?.map(
        (x) => ({ ...x, pb_serial_no: poweredBuildSN }) // Adding is important here because we'll need it for creating transaction
      );

      const sortedPoweredBuildItemsToTransactionLookAlikeItems = sortProductsByCategory(
        poweredBuildItemsToTransactionLookAlikeItems
      );

      const poweredBuildsTotal = sortedPoweredBuildItemsToTransactionLookAlikeItems.reduce(
        (acc, item) => acc + (item?.retail_price || 0),
        0
      );

      const poweredBuildResponseThatLooksLikeTransactionGet: GetTransactionsViaTransactionNoResponse = {
        success: true,
        items: sortedPoweredBuildItemsToTransactionLookAlikeItems,
        total_price: poweredBuildsTotal
      };

      const branchIDBasedOnPoweredBuild = pcBundleItems?.[0]?.branch_id;

      const branchBasedOnPoweredBuild = taptapAddCustomerFields
        .find((x) => x.key === taptapCustomerField.BRANCH_RELEASED)
        ?.options?.find((x: any) => x.branch_id === branchIDBasedOnPoweredBuild)
        ?.value;

      if (branchBasedOnPoweredBuild) {
        newFormDataInitial[
          taptapCustomerField.BRANCH_RELEASED
        ] = branchBasedOnPoweredBuild;
      }

      if (poweredBuildResponseThatLooksLikeTransactionGet?.total_price) {
        newFormDataInitial[taptapCustomerField.TOTAL_AMOUNT] =
          poweredBuildResponseThatLooksLikeTransactionGet?.total_price;
      }

      setPoweredBuildSNResponse(
        poweredBuildResponseThatLooksLikeTransactionGet
      );
    } catch (error) {
      console.error(error);
      snackBar.show({
        severity: 'error',
        message: 'Something went wrong on general for. Consult the dev.',
        useSound: true
      });
      return;
    }

    // Clear transaction related responses and inputs to avoid conflicts
    setTransactionNoInput('');
    setTransactionInfoResponse(undefined);
    setTransactionPaymentsResponse(undefined);
    setTransactionsInfoLoading(false);

    setQuotationItemsConcerns(undefined);
    setQuotationResponse(undefined);
    setTestingCheckedItems([]);

    setPoweredBuildSNInput('');

    formik.setValues({ ...formik.values, ...newFormDataInitial });
  };

  // eslint-disable-next-line no-unused-vars
  const onInputOfQueueingCustomerUUID = async (uuidArg: string) => {
    const uuid = uuidArg?.trim();
    const { data, error } = await SupabaseClientService.from('queued_customers')
      .select(`*`)
      .eq('uuid', uuid)
      .single();

    if (error) {
      console.error('Error Fetching UUID Queueing Customer Data', error);
      snackBar.show({
        severity: 'error',
        message: 'Error Fetching UUID Queueing Customer Data'
      });
      return;
    }

    const dataFromCustomerQueue = {
      [taptapCustomerField.FIRST_NAME]: data?.first_name,
      [taptapCustomerField.LAST_NAME]: data?.last_name,
      [taptapCustomerField.CONTACT_NO]: data?.contact_no,
      [taptapCustomerField.EMAIL]: data?.email,
      [taptapCustomerField.ADDRESS]: data?.address,
      [taptapCustomerField.BRANCH_RELEASED]: data?.branch_released
    };

    formik.setValues({ ...formik.values, ...dataFromCustomerQueue });
  };

  const onInputOfTransactionNo = async () => {
    const transactionNo: string = transactionNoInput?.trim();
    setTransactionsInfoLoading(true);

    const { data, error } = await taptapGetCustomerDetailsViaTransactionNo(
      transactionNo
    );

    if (error) {
      console.error('Error Fetching Transaction Data', error);
      snackBar.show({
        severity: 'error',
        message:
          'Error Fetching Transaction Data From Live Transactions Manager'
      });
      resetFormAlongWithOtherStates();
      return;
    }

    if (data?.length > 0) {
      snackBar.show({
        severity: 'error',
        message: 'Transaction Already Exist in Live Transactions Manager'
      });
      resetFormAlongWithOtherStates();
      return;
    }

    const transactionInfoResp: any = unwrapResult(
      await dispatch(
        transactionActions.getTransactionViaTransactionNo(transactionNo)
      )
    );

    if (
      !transactionInfoResp?.success ||
      isEmpty(transactionInfoResp?.originalData)
    ) {
      snackBar.show({
        severity: 'error',
        message: 'Error Fetching Transaction Data'
      });
      resetFormAlongWithOtherStates();
      return;
    }
    if (transactionInfoResp?.originalData?.items?.length <= 0) {
      snackBar.show({
        severity: 'error',
        message: 'There is not even a product in this transaction?'
      });
      resetFormAlongWithOtherStates();
      return;
    }

    const transactionInfo: GetTransactionsViaTransactionNoResponse =
      transactionInfoResp?.originalData;
    const transactionInfoItems = transactionInfo?.items || [];

    const allDeleted = transactionInfoItems.every(
      (item) => item.isDeleted === 1
    );

    if (allDeleted) {
      snackBar.show({
        severity: 'error',
        message: 'This transaction is already voided?'
      });
      resetFormAlongWithOtherStates();
      return;
    }

    const transactionPaymentsResp = unwrapResult(
      await dispatch(
        transactionPaymentActions.getTransactionPaymentsThunk(transactionNo)
      )
    );

    setTransactionInfoResponse(transactionInfo);
    setTransactionPaymentsResponse(transactionPaymentsResp?.originalData);

    const newAddFormValues: FormValues = {
      [taptapCustomerField.INTERNAL_QUOTATION_ID]: null, // Syempre walang quotation id kasi direct na eh.
      [taptapCustomerField.FIRST_NAME]: transactionInfo?.customer?.first_name,
      [taptapCustomerField.LAST_NAME]: transactionInfo?.customer?.last_name,
      [taptapCustomerField.CONTACT_NO]: transactionInfo?.customer?.contact_no,
      [taptapCustomerField.EMAIL]: transactionInfo?.customer?.email,
      [taptapCustomerField.ADDRESS]: transactionInfo?.customer?.address,
      [taptapCustomerField.REMARKS]: transactionInfo?.transaction_remarks
        ? `Automated Message From Transaction No: ${transactionInfo?.transaction_remarks}`
        : '',
      [taptapCustomerField.TOTAL_AMOUNT]: transactionInfo?.sub_total_price,
      [taptapCustomerField.INTERNAL_TRANSACTION_NO]:
        transactionInfo?.transaction_no,
      [taptapCustomerField.INTERNAL_CUSTOMER_ID]: transactionInfo?.customer?.id
    };

    if (transactionPaymentsResp?.originalData?.success) {
      newAddFormValues.balance =
        transactionPaymentsResp?.originalData?.total_balance || 0;
      newAddFormValues.paid_amount =
        transactionPaymentsResp?.originalData?.total_paid_amount || 0;
    } else {
      newAddFormValues.balance = transactionInfo?.sub_total_price;
      newAddFormValues.paid_amount = 0;
    }

    formik.setValues({ ...formik.values, ...newAddFormValues });
    setTransactionsInfoLoading(false);
  };

  const fetchQuotationProductsAvailability = async (
    quotationResponseArg?: TemplateDetails
  ) => {
    if (!quotationResponseArg) {
      snackBar.show({
        severity: 'error',
        message:
          'Failed to check products availability, Please try again or contact IT support'
      });
      return;
    }
    const { products } = quotationResponseArg;
    const cleanItems = products?.[0]
      ? products.map((i: TemplateProductDetails) => ({
          ...i,
          ...(i.product.product_id === null && { custom: true }) // band aid assume custom if product id is null
        }))
      : [];
    const response = unwrapResult(
      await dispatch(
        quotationActions.checkQuotationAvailabilityThunk({
          items: cleanItems,
          branch_id: quotationResponseArg?.branch_id
        })
      )
    );

    return response;
  };

  const onBranchFieldSetBasedOnQuotation = (
    quotationResponseArg: TemplateDetails
  ) => {
    const branchBasedOnQuotation = taptapAddCustomerFields
      .find((x) => x.key === taptapCustomerField.BRANCH_RELEASED)
      ?.options?.find(
        (x: any) => x.branch_id === quotationResponseArg?.branch_id
      )?.value;

    if (branchBasedOnQuotation) {
      formik.setFieldValue(
        taptapCustomerField.BRANCH_RELEASED,
        branchBasedOnQuotation
      );
    }
  };

  const fetchQuotationProducts = async (internal_quotation_id: number) => {
    setQuotationProductsLoading(true);
    const response = unwrapResult(
      await dispatch(
        quotationActions.getTemplateDetailsThunk({
          template_id: Number(internal_quotation_id)
        })
      )
    );

    if (!response?.success) {
      snackBar.show({
        severity: 'error',
        message: 'Error Fetching Quotation Data'
      });
      setQuotationProductsLoading(false);
      return;
    }
    if (response?.originalData?.data) {
      const origQuotationResponse = response?.originalData?.data;
      const QuoteWithChecker = await fetchQuotationProductsAvailability(
        origQuotationResponse
      );

      if (!response.success) {
        snackBar.show({
          severity: 'error',
          message:
            Object.values(response.errors).join(', ') ??
            response.message ??
            'Failed to fetch quotation products availability, Please try again later or contact IT Support.'
        });
      }
      origQuotationResponse.products =
        QuoteWithChecker?.originalData?.quote_items;
      setQuotationResponse(origQuotationResponse);
      setTestingCheckedItems([]);
      onBranchFieldSetBasedOnQuotation(origQuotationResponse);

      formik.setFieldValue('total_amount', origQuotationResponse?.total_amount);
    }

    setQuotationProductsLoading(false);
  };

  const setInitialFormValues = (
    someValuesAsideConstInitialValues: FormValues
  ) => {
    formik.setValues({
      ...initialValues,
      ...someValuesAsideConstInitialValues
    });

    if (someValuesAsideConstInitialValues?.internal_quotation_id) {
      fetchQuotationProducts(
        someValuesAsideConstInitialValues?.internal_quotation_id
      );
    }
  };

  const resetState = () => {
    formik.resetForm();
    pendingInternalCustomerRef.current = undefined;
    dispatch(taptapCustomerActions.clearPendingInternalCustomer());
    setRetryFailure(undefined);
    setIsRetryDialogVisible(false);
    setTestingCheckedItems([]);
  };

  const createRequiredTestingComment = async (customerId?: number) => {
    if (
      !customerId ||
      formik.values[taptapCustomerField.SERVICE_TYPE] ===
        taptapServiceType.FOR_BUILD ||
      testingCheckedItems.length <= 0
    ) {
      return;
    }

    const testingItemsComment = testingCheckedItems
      .map((item) => {
        const quantityLabel = item.quantity ? `${item.quantity}x ` : '';
        return `- ${quantityLabel}${item.product || 'Unnamed item'}`;
      })
      .join('\n');

    const { error } = await addTicketComment({
      customer_id: customerId,
      full_name: 'System',
      comment: `Please test the following item(s):\n${testingItemsComment}`
    });

    if (error) {
      console.error('Failed to create required testing comment:', error);
      snackBar.show({
        severity: 'error',
        message: 'Customer created, but failed to add testing comment.'
      });
    }
  };

  // When adding a taptap customer some fields have conditions that need to be met before they can appear
  // Here, what we do is remove the value of the field if the condition is not met
  const isSomeConditionalFieldsHaveUselessValues = () => {
    const currentFormikValues: any = cloneDeep(formik?.values);
    taptapAddCustomerFields?.forEach((field) => {
      if (field?.conditional) {
        const dependentValue = currentFormikValues[field.conditional.dependsOn];
        if (!field?.conditional?.value?.includes(dependentValue)) {
          formik.setFieldValue(field.key, initialValues[field.key]);
          currentFormikValues[field.key] = initialValues[field.key];
        }
      }
    });

    return currentFormikValues;
  };

  const validateQuotationStocks = async (newTapTapCustomerData: FormValues) => {
    if (newTapTapCustomerData?.internal_quotation_id && quotationResponse) {
      const productsFromQuotatationWithoutProductIds = quotationResponse?.products?.filter(
        (product) => isInvalidProduct(product)
      );
      if (
        productsFromQuotatationWithoutProductIds &&
        productsFromQuotatationWithoutProductIds?.length > 0
      ) {
        const productNames = productsFromQuotatationWithoutProductIds
          .map((x) => x.product?.product_name)
          .join(',');
        snackBar.show({
          severity: 'error',
          message: `Some products from the quotation do not have product IDs. Are they preorders? ${productNames}`
        });
        return false;
      }
    }

    const checkerRes = await fetchQuotationProductsAvailability(
      quotationResponse
    );

    if (checkerRes?.success) {
      const resItems = checkerRes?.originalData?.quote_items || [];
      const hasWarning = resItems?.filter((x) => x.warning_note);

      if (hasWarning?.length) {
        snackBar.show({
          severity: 'error',
          message:
            'Some item(s) might be out of stock. Please check availability.'
        });

        const itemsWithConcerns = resItems?.map(
          (x) => x.product?.product_name || ''
        );
        setQuotationItemsConcerns(itemsWithConcerns);

        const clonedQuotationResponse = cloneDeep(quotationResponse);
        if (clonedQuotationResponse) {
          clonedQuotationResponse.products = resItems;
          setQuotationResponse(clonedQuotationResponse);
        }

        return false;
      }

      return true;
    }

    snackBar.show({
      severity: 'error',
      message:
        checkerRes?.message ||
        'Failed to validate quotation availability. Please try again.'
    });
    showRetryDialog('quotation-validation');
    return false;
  };

  /**
   *
   * @param newCustomerData
   * @returns new customer id if successful api call
   */
  const getNormalizedCustomerIdentity = (customerData: FormValues) => ({
    first_name: `${customerData.first_name || ''}`.trim().toLowerCase(),
    last_name: `${customerData.last_name || ''}`.trim().toLowerCase(),
    contact_no: `${customerData.contact_no || ''}`.trim()
  });

  const getReusableInternalCustomer = (customerData: FormValues) => {
    const normalizedCustomerIdentity = getNormalizedCustomerIdentity(
      customerData
    );
    const reusableCustomer = pendingInternalCustomerRef.current;
    const isSameCustomer =
      reusableCustomer?.first_name === normalizedCustomerIdentity.first_name &&
      reusableCustomer?.last_name === normalizedCustomerIdentity.last_name &&
      reusableCustomer?.contact_no === normalizedCustomerIdentity.contact_no;

    return isSameCustomer ? reusableCustomer : undefined;
  };

  const createInternalCustomer = async (newCustomerData: FormValues) => {
    const normalizedCustomerIdentity = getNormalizedCustomerIdentity(
      newCustomerData
    );
    const reusableCustomer = getReusableInternalCustomer(newCustomerData);

    if (reusableCustomer) {
      return reusableCustomer.id;
    }

    if (pendingInternalCustomerRef.current) {
      pendingInternalCustomerRef.current = undefined;
      dispatch(taptapCustomerActions.clearPendingInternalCustomer());
    }

    let internal_customer_values = {
      first_name: newCustomerData.first_name,
      middle_name: '',
      last_name: newCustomerData.last_name || '',
      email: newCustomerData.email || '',
      address: newCustomerData.address || '',
      social_media: '',
      contact_no: newCustomerData.contact_no || '',
      notes: newCustomerData.remarks || ''
    };

    let createInternalCustomerResponse;

    try {
      createInternalCustomerResponse = unwrapResult(
        await dispatch(
          createCustomerServiceActions.createCustomerServiceThunk(
            internal_customer_values
          )
        )
      );
    } catch (error) {
      console.error('Error creating internal system customer', error);
      snackBar.show({
        severity: 'error',
        message: 'Error creating internal system customer.'
      });
      showRetryDialog('internal-customer');
      return;
    }

    const newInternalCustomerId =
      createInternalCustomerResponse?.originalData?.customer?.id;

    if (!newInternalCustomerId) {
      snackBar.show({
        severity: 'error',
        message: `Error creating internal system customer ${JSON.stringify(
          createInternalCustomerResponse?.message ||
            createInternalCustomerResponse?.errors
        )} `
      });
      showRetryDialog('internal-customer');
    } else {
      const pendingCustomer = {
        id: newInternalCustomerId,
        ...normalizedCustomerIdentity,
        is_reserved: isReserved.current
      };

      // Persist the customer across retries and page reloads. The identity
      // fields prevent a stale customer ID from being reused for another form.
      pendingInternalCustomerRef.current = pendingCustomer;
      dispatch(
        taptapCustomerActions.setPendingInternalCustomer(pendingCustomer)
      );
    }

    return newInternalCustomerId;
  };

  const persistInternalTransactionNo = (internalTransactionNo: string) => {
    const pendingCustomer = pendingInternalCustomerRef.current;

    if (!pendingCustomer) {
      return;
    }

    const updatedPendingCustomer = {
      ...pendingCustomer,
      internal_transaction_no: internalTransactionNo
    };

    pendingInternalCustomerRef.current = updatedPendingCustomer;
    dispatch(
      taptapCustomerActions.setPendingInternalCustomer(updatedPendingCustomer)
    );
  };

  const createTransactionViaTemplate = async (newCustomerData: FormValues) => {
    if (newCustomerData.internal_quotation_id && quotationResponse) {
      let createTransactionResponse;

      try {
        createTransactionResponse = unwrapResult(
          await dispatch(
            transactionActions.createTransactionViaQuotationTemplateThunk({
              template_id: newCustomerData.internal_quotation_id,
              customer_id: newCustomerData.internal_customer_id
            })
          )
        );
      } catch (error) {
        console.error('Error creating internal system transaction', error);
        snackBar.show({
          severity: 'error',
          message: 'Error creating internal system transaction.'
        });
        showRetryDialog('internal-transaction');
        return;
      }

      const internalTransactionNo =
        createTransactionResponse?.originalData?.data?.transaction_no;

      if (
        !internalTransactionNo ||
        !createTransactionResponse?.originalData?.success
      ) {
        const concerns: any =
          createTransactionResponse?.originalData?.data || [];

        if (concerns?.length > 0) {
          setQuotationItemsConcerns(concerns);
        }

        snackBar.show({
          severity: 'error',
          message: `Error creating internal system transaction. ${JSON.stringify(
            createTransactionResponse?.message
          )} ${JSON.stringify(concerns?.join(','))}`
        });
        showRetryDialog('internal-transaction');
        return;
      }

      return internalTransactionNo;
    }
  };

  const createTransactionViaPoweredBuilds = async (
    newCustomerData: FormValues
  ) => {
    const poweredBuildMultipleListingsPayload = poweredBuildSNResponse?.items?.map(
      (pb) => ({
        customer_id: newCustomerData?.internal_customer_id,
        serial_no: pb?.serial_no,
        product_name: pb?.product_name,
        quantity: pb?.quantity,
        product_id: pb?.product_id,
        amount: pb?.retail_price,
        category_name: pb?.category_name,
        pb_serial_no: pb?.pb_serial_no,
        branch_name: pb?.branch_name
      })
    );

    if (!poweredBuildMultipleListingsPayload) {
      return;
    }

    const pcBundleComponentGroupSN =
      poweredBuildSNResponse?.items?.[0]?.pb_serial_no;

    if (!pcBundleComponentGroupSN) {
      return;
    }

    const createTransactionViaPBPayload = {
      multipleListings: poweredBuildMultipleListingsPayload,
      pc_bundle_component_group_serial_no: pcBundleComponentGroupSN,
      transaction_remarks: 'Created Via TapTap, Via Powered Builds'
    };

    if (poweredBuildSNResponse) {
      let addTransactionViaPBRes;

      try {
        addTransactionViaPBRes = unwrapResult(
          await dispatch(
            transactionActions.createTransactionThunk(
              createTransactionViaPBPayload
            )
          )
        );
      } catch (error) {
        console.error('Error creating internal system transaction', error);
        snackBar.show({
          severity: 'error',
          message: 'Error creating internal system transaction.'
        });
        showRetryDialog('internal-transaction');
        return;
      }

      if (addTransactionViaPBRes?.success) {
        return addTransactionViaPBRes?.originalData?.transaction_no;
      }

      if (!addTransactionViaPBRes?.success) {
        snackBar.show({
          severity: 'error',
          message: `Error creating internal system transaction. ${JSON.stringify(
            addTransactionViaPBRes?.message
          )}`
        });
        showRetryDialog('internal-transaction');
        return;
      }
    }
  };

  const handleFormSubmit = async (values: any) => {
    if (isLoading.current) {
      return;
    }

    if (!navigator.onLine) {
      showRetryDialog('network');
      return;
    }

    isLoading.current = true;

    // Check if formik fields are valid
    if (formik?.errors && Object.keys(formik?.errors)?.length > 0) {
      isLoading.current = false;

      snackBar.show({
        severity: 'error',
        message: JSON.stringify(formik?.errors)
      });
      return;
    }

    let newTapTapCustomerData: TapTapCustomerData = values;

    // Check if there are conditional fields that have useless values
    const thereIsAConditionalFieldThatHasAUselessValue = isSomeConditionalFieldsHaveUselessValues();
    newTapTapCustomerData = thereIsAConditionalFieldThatHasAUselessValue;

    const isSalesTransfer = taptapServiceTypesWithSalesTransferBranch.includes(
      newTapTapCustomerData[taptapCustomerField.SERVICE_TYPE]
    );

    const salesTransferComment = isSalesTransfer
      ? buildSalesTransferComment(
          newTapTapCustomerData[taptapCustomerField.KIND_OF_SALES_TRANSFER]
        )
      : '';

    if (isSalesTransfer) {
      if (!newTapTapCustomerData[taptapCustomerField.KIND_OF_SALES_TRANSFER]) {
        snackBar.show({
          severity: 'error',
          message: 'Kind of Sales Transfer is required.'
        });
        isLoading.current = false;
        return;
      }
    } else {
      newTapTapCustomerData[taptapCustomerField.KIND_OF_SALES_TRANSFER] = '';
    }

    if (newTapTapCustomerData?.internal_quotation_id && quotationResponse) {
      const reusableTransactionNo = getReusableInternalCustomer(
        newTapTapCustomerData
      )?.internal_transaction_no;

      /** Check if there are quotation items that are out of stock or preorder */
      if (!reusableTransactionNo) {
        const isQuotationValid = await validateQuotationStocks(
          newTapTapCustomerData
        );
        if (!isQuotationValid) {
          isLoading.current = false;
          return;
        }
      }

      // Create a customer based on the quotation if there is a quotation
      const internalCustomerId = await createInternalCustomer(
        newTapTapCustomerData
      );
      if (!internalCustomerId) {
        isLoading.current = false;
        return;
      }
      // IMPORTANT!!! Set the internal_customer_id to the newTapTapCustomerData
      newTapTapCustomerData.internal_customer_id = internalCustomerId;

      // Create a transaction based on the quotation if there is a quotation
      const internalTransactionNo =
        reusableTransactionNo ||
        (await createTransactionViaTemplate(newTapTapCustomerData));

      if (!internalTransactionNo) {
        // A failed/aborted client response does not guarantee that the backend
        // stopped processing the transaction. Keep the customer so a late
        // transaction can never point to a deleted customer. A retry will
        // reuse the same internal customer instead of creating a duplicate.
        isLoading.current = false;

        return;
      }

      // IMPORTANT!!! Set the internal_transaction_no to the newTapTapCustomerData
      newTapTapCustomerData.internal_transaction_no = internalTransactionNo;
      persistInternalTransactionNo(internalTransactionNo);
      newTapTapCustomerData.balance = newTapTapCustomerData.total_amount;
    }

    // Ibig sabihin neto. Tinatry ni user na mag add ng customer sa taptap via powered builds SN
    if (poweredBuildSNResponse) {
      // Create a customer based for PB
      const internalCustomerId = await createInternalCustomer(
        newTapTapCustomerData
      );

      if (!internalCustomerId) {
        isLoading.current = false;
        return;
      }
      // IMPORTANT!!! Set the internal_customer_id to the newTapTapCustomerData
      newTapTapCustomerData.internal_customer_id = internalCustomerId;

      // Create a transaction based on the POWERED BUILDS RESPONSE if it exists
      const internalTransactionNo =
        getReusableInternalCustomer(newTapTapCustomerData)
          ?.internal_transaction_no ||
        (await createTransactionViaPoweredBuilds(newTapTapCustomerData));

      if (!internalTransactionNo) {
        // Keep and reuse the customer on retry. The transaction request may
        // still complete after a timeout or aborted client response.
        isLoading.current = false;

        return;
      }

      // IMPORTANT!!! Set the internal_transaction_no to the newTapTapCustomerData
      newTapTapCustomerData.internal_transaction_no = internalTransactionNo;
      persistInternalTransactionNo(internalTransactionNo);
      newTapTapCustomerData.balance = newTapTapCustomerData.total_amount;
    }

    // Next time do this as a util..
    if (!newTapTapCustomerData?.shipping_fee) {
      newTapTapCustomerData.shipping_fee = 0;
      formik.setFieldValue(taptapCustomerField.SHIPPING_FEE, 0);
    }
    if (!newTapTapCustomerData?.total_amount) {
      newTapTapCustomerData.total_amount = 0;
      formik.setFieldValue(taptapCustomerField.TOTAL_AMOUNT, 0);
    }

    // Tangina may ganitong logic pala.
    // If total_amount is more than 0 and balance is 0,
    // then set balance based on total_amount
    if (
      newTapTapCustomerData?.total_amount > 0 &&
      (newTapTapCustomerData?.balance === 0 || !newTapTapCustomerData?.balance)
    ) {
      newTapTapCustomerData.balance = newTapTapCustomerData.total_amount;
    }

    if (salesTransferTicketId) {
      const { error: updateTicketError } = await SupabaseClientService.from(
        'customers'
      )
        .update({
          [taptapCustomerField.FIRST_NAME]: newTapTapCustomerData.first_name,
          [taptapCustomerField.LAST_NAME]: newTapTapCustomerData.last_name,
          [taptapCustomerField.CONTACT_NO]: newTapTapCustomerData.contact_no,
          [taptapCustomerField.EMAIL]: newTapTapCustomerData.email,
          [taptapCustomerField.ADDRESS]: newTapTapCustomerData.address,
          [taptapCustomerField.REMARKS]: newTapTapCustomerData.remarks,
          [taptapCustomerField.INTERNAL_QUOTATION_ID]:
            newTapTapCustomerData.internal_quotation_id,
          [taptapCustomerField.INTERNAL_TRANSACTION_NO]:
            newTapTapCustomerData.internal_transaction_no,
          [taptapCustomerField.INTERNAL_CUSTOMER_ID]:
            newTapTapCustomerData.internal_customer_id,
          [taptapCustomerField.TOTAL_AMOUNT]:
            newTapTapCustomerData.total_amount,
          [taptapCustomerField.BALANCE]: newTapTapCustomerData.balance,
          [taptapCustomerField.PAID_AMOUNT]: newTapTapCustomerData.paid_amount,
          [taptapCustomerField.TRIAL_OS]: newTapTapCustomerData.trial_os,
          [taptapCustomerField.PURCHASE_PURPOSE]:
            newTapTapCustomerData.purchase_purpose,
          [taptapCustomerField.REFERRAL_SOURCE]:
            newTapTapCustomerData.referral_source,
          [taptapCustomerField.QUOTATION]: newTapTapCustomerData.quotation,
          [taptapCustomerField.STATUS]: taptapStatuses.FOR_PAYMENT
        })
        .eq('id', salesTransferTicketId);

      if (updateTicketError) {
        snackBar.show({
          severity: 'error',
          message: JSON.stringify(updateTicketError)
        });
        isLoading.current = false;
        isReserved.current = false;
        return;
      }

      const updatedBy =
        `${userDetails?.first_name || ''} ${userDetails?.last_name ||
          ''}`.trim() || 'System';

      const { error: ticketLogError } = await taptapCreateTicketLogs({
        ticket_id: salesTransferTicketId,
        status: taptapStatuses.FOR_PAYMENT,
        updated_by: updatedBy
      });

      if (ticketLogError) {
        console.error(
          'Failed to create sales transfer ticket log:',
          ticketLogError
        );
      }

      snackBar.show({
        severity: 'success',
        message: 'Sales transfer transaction attached successfully'
      });

      sleep(2000);
      navigate(`/app/customer-taptap/${salesTransferTicketId}`);

      isLoading.current = false;
      isReserved.current = false;
      resetState();
      return;
    }

    if (isSalesTransferAttachMode) {
      snackBar.show({
        severity: 'error',
        message:
          'Sales transfer attach mode cannot create a new TapTap customer.'
      });
      isLoading.current = false;
      isReserved.current = false;
      return;
    }

    const initialTicketStatus = resolveInitialTicketStatus({
      isReservedTicket: isReserved.current,
      values: newTapTapCustomerData
    });

    // A timed-out insert can still finish in Supabase after the browser reports
    // an error. Always check the transaction number before retrying the insert
    // so an already-created ticket is opened instead of duplicated.
    if (newTapTapCustomerData.internal_transaction_no) {
      let existingTicketResponse;

      try {
        existingTicketResponse = await taptapGetCustomerDetailsViaTransactionNo(
          newTapTapCustomerData.internal_transaction_no
        );
      } catch (verificationError) {
        console.error(
          'Error verifying existing TapTap ticket',
          verificationError
        );
        snackBar.show({
          severity: 'error',
          message: 'Unable to verify if the TapTap ticket already exists.'
        });
        showRetryDialog('taptap-verification');
        isLoading.current = false;
        isReserved.current = false;
        return;
      }

      if (existingTicketResponse.error) {
        console.error(
          'Error verifying existing TapTap ticket',
          existingTicketResponse.error
        );
        snackBar.show({
          severity: 'error',
          message: 'Unable to verify if the TapTap ticket already exists.'
        });
        showRetryDialog('taptap-verification');
        isLoading.current = false;
        isReserved.current = false;
        return;
      }

      const existingTicketId = existingTicketResponse.data?.[0]?.id;

      if (existingTicketId) {
        snackBar.show({
          severity: 'success',
          message: 'TapTap ticket already exists. Opening the existing ticket.'
        });
        isLoading.current = false;
        isReserved.current = false;
        resetState();
        navigate(`/app/customer-taptap/${existingTicketId}`);
        return;
      }
    }

    let ticketInsertResponse;

    try {
      ticketInsertResponse = await SupabaseClientService.from('customers')
        .insert({
          ...newTapTapCustomerData,
          [taptapCustomerField.STATUS]: initialTicketStatus,
          /**Add tagging for taptap reeserve transaction
           * User should do downpayment
           */
          ...(isReserved.current && { tag: 'FOR RESERVED' })
        })
        .select('id');
    } catch (insertError) {
      console.error('Error creating TapTap ticket', insertError);
      snackBar.show({
        severity: 'error',
        message: 'Error creating TapTap ticket.'
      });
      showRetryDialog('taptap-ticket');
      isLoading.current = false;
      isReserved.current = false;
      return;
    }

    const { data, error } = ticketInsertResponse;

    if (error) {
      snackBar.show({
        severity: 'error',
        message: JSON.stringify(error)
      });
      showRetryDialog('taptap-ticket');

      // Keep both internal records. The persisted customer and transaction IDs
      // let a retry create only the missing TapTap ticket.

      isLoading.current = false;
      isReserved.current = false; // <-- reset

      console.error(error);
      return;
    }

    if (data?.[0]?.id) {
      const { error: ticketLogError } = await taptapCreateTicketLogs({
        ticket_id: data[0].id,
        status: initialTicketStatus,
        updated_by:
          `${userDetails?.first_name || ''} ${userDetails?.last_name ||
            ''}`.trim() || 'System'
      });

      if (ticketLogError) {
        console.error('Failed to create initial ticket log:', ticketLogError);
      }

      await createRequiredTestingComment(data[0].id);
    }

    if (salesTransferComment) {
      const { error: salesTransferCommentError } = await addTicketComment({
        customer_id: data[0].id,
        user_id: undefined,
        full_name: 'System',
        comment: salesTransferComment,
        tag: SALES_TRANSFER_COMMENT_TAG
      });

      if (salesTransferCommentError) {
        console.error(
          'Failed to create sales transfer ticket comment:',
          salesTransferCommentError
        );
      }
    }

    // Check if may payments from transactions
    if (
      transactionPaymentsResponse &&
      transactionPaymentsResponse?.data &&
      transactionPaymentsResponse?.data?.length > 0
    ) {
      // Check if may INTERNAL_TRANSACTION_NO and data
      if (
        formik.values[taptapCustomerField.INTERNAL_TRANSACTION_NO] &&
        data?.[0]?.id
      ) {
        onTapTapCustomerPaymentAddFromTransactionPayments(
          transactionPaymentsResponse.data,
          {
            transaction_no:
              formik.values[taptapCustomerField.INTERNAL_TRANSACTION_NO],
            taptap_customer_id: data?.[0]?.id
          }
        );
      }
    }

    snackBar.show({
      severity: 'success',
      message: 'Customer added successfully'
    });

    if (data?.[0]?.id) {
      sleep(2000);
      navigate(`/app/customer-taptap/${data?.[0]?.id}`);
    }

    isLoading.current = false;
    isReserved.current = false; // <-- reset

    resetState();
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleFormSubmit,
    validationSchema: formSchema
  });
  const selectedServiceType = formik.values[taptapCustomerField.SERVICE_TYPE];
  const selectedSource = formik.values[taptapCustomerField.SOURCE];
  useEffect(() => {
    pendingInternalCustomerRef.current = pendingInternalCustomer;

    if (pendingInternalCustomer) {
      setRetryFailure(
        (currentFailure) =>
          currentFailure || {
            step: pendingInternalCustomer.internal_transaction_no
              ? 'taptap-ticket'
              : 'internal-transaction',
            reserved: Boolean(pendingInternalCustomer.is_reserved)
          }
      );
    }
  }, [pendingInternalCustomer]);

  useEffect(() => {
    if (selectedServiceType === taptapServiceType.FOR_BUILD) {
      setTestingCheckedItems([]);
    }
  }, [selectedServiceType]);

  useEffect(() => {
    formik.setFieldValue(
      taptapCustomerField.STATUS,
      resolveInitialTicketStatus({
        isReservedTicket: isReserved.current,
        values: formik.values
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceType, selectedSource]);

  const taptapCustomerToBePayments = useMemo(() => {
    const data = transactionPaymentsToTapTapCustomerPaymentPayloadTransformer(
      transactionPaymentsResponse?.data,
      {
        transaction_no: formik.values?.internal_transaction_no,
        taptap_customer_id: 0
      }
    );

    return data || [];
  }, [formik.values.internal_transaction_no, transactionPaymentsResponse]);

  useEffect(() => {
    const search = location.search;
    const params = new URLSearchParams(search);

    const paramsKeyValuePair: any = {};
    params.forEach((value, key) => {
      paramsKeyValuePair[key] = value;
    });

    const validKeys = taptapAddCustomerFields.map((item) => item.key);

    // Filter the data object to include only valid keys
    const filteredData = Object.keys(paramsKeyValuePair)
      .filter((key) => validKeys.includes(key)) // Keep keys that are in the schema
      .reduce((acc: any, key) => {
        acc[key] = paramsKeyValuePair[key];
        return acc;
      }, {});

    const salesTransferTicketId = getSalesTransferTicketIdFromSearch(search);

    if (salesTransferTicketId) {
      SupabaseClientService.from('customers')
        .select('*')
        .eq('id', salesTransferTicketId)
        .single()
        .then(({ data, error }) => {
          if (error || !data) {
            snackBar.show({
              severity: 'error',
              message: 'Failed to fetch sales transfer request ticket'
            });
            setInitialFormValues(filteredData);
            return;
          }

          setInitialFormValues({
            ...data,
            ...filteredData
          });
        });
      return;
    }

    setInitialFormValues(filteredData);
    // formik.setValues({ ...initialValues, ...filteredData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidateAndSubmit = async (reserved: boolean) => {
    const validateForm = await formik.validateForm();
    const shouldRequireTrialOs = [
      taptapServiceType.FOR_BUILD,
      taptapServiceType.BUILD_SALES_TRANSFER
    ].includes(formik.values[taptapCustomerField.SERVICE_TYPE]);
    const visibleValidateForm = { ...validateForm };

    if (!shouldRequireTrialOs) {
      delete visibleValidateForm[taptapCustomerField.TRIAL_OS];
    }

    if (visibleValidateForm && Object.keys(visibleValidateForm).length > 0) {
      const errorFields = Object.keys(visibleValidateForm).join(', ');
      const message = `Please check the following fields: ${errorFields}`;
      snackBar.show({ severity: 'error', message, useSound: true });
      return;
    }

    if (isLoading.current) {
      snackBar.show({
        severity: 'error',
        message: 'Currently loading. Please wait.'
      });
      return;
    }

    isReserved.current = reserved; // <-- set bago mag-submit
    formik.handleSubmit();
  };

  const handleRetryCreation = () => {
    const reserved = retryFailure?.reserved || false;
    setIsRetryDialogVisible(false);
    handleValidateAndSubmit(reserved);
  };

  return (
    <Page className={classes.root} title="Add to Live Customers Manager">
      <Container
        maxWidth={false}
        style={{
          opacity:
            isLoading.current ||
            quotationProductsLoading ||
            transactionsInfoLoading
              ? 0.3
              : 1
        }}
      >
        {formik?.values?.is_deleted && (
          <Alert
            hidden
            severity="error"
            style={{ marginTop: 10, marginBottom: 10 }}
          >
            <Typography variant="h3">
              CUSTOMER IS ALREADY DELETED. YOU CANNOT EDIT THIS CUSTOMER
              ANYMORE.
            </Typography>
          </Alert>
        )}
        <LoaderBar isLoading={isLoading.current} />
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Typography color="textPrimary" gutterBottom variant="h3">
            {isSalesTransferAttachMode
              ? 'Attach Sales Transfer Transaction'
              : 'Add Live Customer'}{' '}
            {`- ${formik?.values?.first_name} ${formik?.values?.last_name}`}
          </Typography>
        </Box>

        {!formik?.values?.internal_quotation_id &&
          !poweredBuildSNInput &&
          !poweredBuildSNResponse && (
            <Card elevation={5} style={{ marginTop: 12, marginBottom: 16 }}>
              <CardHeader title="ONLY ENTER EXISTING TRANSACTION NO IF IT STILL NOT LINKED TO A CURRENT REALTIME CUSTOMER" />
              <Divider />
              <CardContent style={{ padding: 20 }}>
                <TextField
                  size="small"
                  fullWidth
                  id={'transaction_no'}
                  variant="outlined"
                  value={transactionNoInput}
                  label="Existing Transaction No"
                  onChange={(e) =>
                    setTransactionNoInput(e.target.value?.trim())
                  }
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter') {
                      onInputOfTransactionNo();
                    }
                  }}
                  helperText="Enter an existing transaction no to prefill the form"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
              </CardContent>
              <LoaderBar isLoading={transactionsInfoLoading} />
            </Card>
          )}

        {formik?.values?.internal_quotation_id && // Pag may quotation id na wag na ipakita input ng PB SN
        transactionInfoResponse ? null : ( // Pag may transactionInfoResponse na wag na ipakita input ng PB SN
          <Card elevation={5} style={{ marginTop: 12, marginBottom: 16 }}>
            <CardHeader title="Add Customer Via Powered Builds" />
            <Divider />
            <CardContent style={{ padding: 20 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                label="Powered Builds SN"
                value={poweredBuildSNInput}
                onChange={(e) => setPoweredBuildSNInput(e.target.value?.trim())}
                onKeyDown={(e: any) => {
                  if (e?.key === 'Enter') {
                    onInputOfPoweredBuildsSN(e.target.value);
                  }
                }}
                helperText="Enter Powered Builds SN"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PowerIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </CardContent>
            <LoaderBar isLoading={transactionsInfoLoading} />
          </Card>
        )}

        {!transactionInfoResponse && // Pag may transactionInfoResponse na wag na ipakita input ng PB SN
          poweredBuildSNResponse && (
            <TapTapPowerBuildsCard
              isLoading={transactionsInfoLoading} // TODO: Adjust loading
              poweredBuildDataThatLooksLikeTransaction={poweredBuildSNResponse}
            />
          )}

        {formik?.values?.internal_transaction_no && transactionInfoResponse && (
          <TapTapTransactionsCard
            purpose="preview"
            onRefreshTransaction={() => {}}
            isLoading={transactionsInfoLoading}
            transactionInfo={transactionInfoResponse}
          />
        )}

        {transactionPaymentsResponse &&
        transactionPaymentsResponse?.data && // FUCK THIS TYPESCRIPT
        transactionPaymentsResponse?.data?.length > 0 ? (
          <TapTapPaymentsCard
            purpose="preview"
            isLoading={false}
            data={taptapCustomerToBePayments}
            transactionNo={formik?.values?.internal_transaction_no}
            currentFormikValues={formik.values}
          />
        ) : null}

        {formik?.values?.internal_quotation_id && quotationResponse ? (
          <TapTapQuotationCard
            initialCollapse
            quotationResponse={quotationResponse}
            internalQuotationId={formik?.values?.internal_quotation_id}
            quotationProductsLoading={quotationProductsLoading}
            checkedItems={testingCheckedItems}
            onCheckedItemsChange={setTestingCheckedItems}
            isTestingCheckboxDisabled={
              selectedServiceType === taptapServiceType.FOR_BUILD
            }
            fetchQuotationProducts={() =>
              fetchQuotationProducts(formik?.values?.internal_quotation_id)
            }
          />
        ) : null}

        <Box mt={3}>
          <TapTapForm
            formFields={taptapAddCustomerFields}
            formikHookRef={formik}
          />
        </Box>

        {retryFailure && !isRetryDialogVisible ? (
          <Alert severity="warning" style={{ marginTop: 20, marginBottom: 12 }}>
            <Typography variant="subtitle1">
              {retryFailureMessages[retryFailure.step].title}
            </Typography>
            <Typography variant="body2">
              {retryFailureMessages[retryFailure.step].description} Press Add
              Customer below to retry.
            </Typography>
          </Alert>
        ) : null}

        <Grid container spacing={5}>
          <Grid
            item
            xs={12}
            md={isSalesTransferAttachMode ? 12 : 6}
            lg={isSalesTransferAttachMode ? 12 : 6}
          >
            <LoadingButton
              fullWidth
              onClick={() =>
                handleValidateAndSubmit(retryFailure?.reserved || false)
              }
              color="primary"
              variant="contained"
              height={50}
              disabled={isLoading.current}
              loading={isLoading.current}
              title={
                isSalesTransferAttachMode
                  ? 'Update Sales Transfer Ticket'
                  : 'Add Customer'
              }
            />
          </Grid>

          {!isSalesTransferAttachMode ? (
            <Grid item xs={12} md={6} lg={6}>
              <LoadingButton
                fullWidth
                onClick={() => handleValidateAndSubmit(true)}
                color="primary"
                variant="outlined"
                height={50}
                disabled={isLoading.current}
                loading={isLoading.current}
                title="Add customer and tag as reserved transaction"
                startIcon={<LocalOfferIcon />}
              />
            </Grid>
          ) : null}
        </Grid>
      </Container>
      <AlertDialog
        title="Some items are out of stock"
        isVisible={
          quotationItemsConcerns && quotationItemsConcerns?.length > 0 // FUCK THIS SHITTY TYPESCRIPT
            ? true
            : false
        }
        subTitle={` ${quotationItemsConcerns?.join(', \r !!! ')}`}
        handleClose={() => setQuotationItemsConcerns(undefined)}
      />
      <AlertDialog
        title={
          retryFailure
            ? retryFailureMessages[retryFailure.step].title
            : 'Creation failed'
        }
        isVisible={Boolean(retryFailure) && isRetryDialogVisible}
        subTitle={
          retryFailure
            ? retryFailureMessages[retryFailure.step].description
            : undefined
        }
        handleClose={() => setIsRetryDialogVisible(false)}
        customButtons={
          <>
            <Button
              onClick={() => setIsRetryDialogVisible(false)}
              color="default"
            >
              Retry later
            </Button>
            <Button onClick={handleRetryCreation} color="primary" autoFocus>
              Retry now
            </Button>
          </>
        }
      />
    </Page>
  );
};

export default AddTapTapCustomer;

const formSchema = yup.object().shape({
  [taptapCustomerField.FIRST_NAME]: yup
    .string()
    .required('First Name is required'),
  [taptapCustomerField.LAST_NAME]: yup
    .string()
    .required('Last Name is required'),
  [taptapCustomerField.BRANCH_RELEASED]: yup
    .string()
    .required('Branch is required'),
  [taptapCustomerField.SOURCE]: yup.string().required('Source is required'),
  [taptapCustomerField.COURIER]: yup.string().required('Courier is required'),
  [taptapCustomerField.ADDRESS]: yup.string().required('Address is required'),
  [taptapCustomerField.CONTACT_NO]: yup
    .string()
    .nullable()
    .matches(/\d+$/, 'Contact number must be numeric'),
  [taptapCustomerField.EMAIL]: optionalEmailSchema,
  [taptapCustomerField.IS_AGREE_TNC]: yup
    .boolean()
    .required('You must accept the terms and conditions')
    .oneOf([true], 'You must accept the terms and conditions'),
  [taptapCustomerField.TOTAL_AMOUNT]: yup
    .number()
    .required('Total Amount is required')
    .typeError('Total Amount must be a number')
    .positive('Total Amount must be greater than zero'),
  [taptapCustomerField.PURCHASE_PURPOSE]: yup
    .string()
    .required(`"Purpose of Purchase" is required`),
  [taptapCustomerField.TRIAL_OS]: yup
    .string()
    .when(taptapCustomerField.SERVICE_TYPE, {
      is: (serviceType) =>
        [
          taptapServiceType.FOR_BUILD,
          taptapServiceType.BUILD_SALES_TRANSFER
        ].includes(serviceType),
      then: yup.string().required('Trial OS is required'),
      otherwise: yup
        .string()
        .nullable()
        .notRequired()
    }),
  [taptapCustomerField.KIND_OF_SALES_TRANSFER]: yup
    .string()
    .when(taptapCustomerField.SERVICE_TYPE, {
      is: (serviceType) =>
        taptapServiceTypesWithSalesTransferBranch.includes(serviceType),
      then: yup.string().required('Kind of Sales Transfer is required')
    }),
  [taptapCustomerField.REFERRAL_SOURCE]: yup
    .string()
    .required('Referral Source is required')
});
